import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, LogIn, AlertCircle, Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import { login, login2fa } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const formatApiError = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

const Login = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  // Second factor step
  const [step, setStep] = useState("password"); // password | totp
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);

  const goIn = (me) => {
    setUser(me);
    if (me.must_change_password) {
      navigate("/mot-de-passe", { replace: true });
    } else if (me.totp_setup_required) {
      navigate("/securite/2fa", { replace: true });
    } else {
      navigate(location.state?.from || "/", { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (pending) return;
    setError("");
    setPending(true);
    try {
      const res = await login(email.trim(), password);
      if (res.status === "pending_2fa") {
        setStep("totp");
        setCode("");
      } else {
        goIn(res);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Connexion impossible. Réessayez."));
    } finally {
      setPending(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    if (pending || code.trim().length < 6) return;
    setError("");
    setPending(true);
    try {
      const me = await login2fa(code.trim(), trustDevice);
      goIn(me);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Vérification impossible. Réessayez."));
      setCode("");
    } finally {
      setPending(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <span className="h-14 w-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/25">
            <Phone className="h-7 w-7 text-white" strokeWidth={2.2} />
          </span>
          <h1 className="font-heading font-bold text-slate-900 text-2xl tracking-tight mt-4">Standard Call</h1>
          <p className="text-sm text-slate-500 mt-1">
            {step === "totp" ? "Vérification en deux étapes." : "Connectez-vous pour accéder à votre standard."}
          </p>
        </div>

        {step === "totp" ? (
          <form onSubmit={submitTotp} className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 space-y-4" data-testid="login-totp-form">
            {error && (
              <div data-testid="login-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <Label htmlFor="login-totp" className="text-sm">Code de vérification</Label>
              <Input
                id="login-totp"
                data-testid="login-totp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="mt-1 h-11 text-base text-center tracking-[0.3em] font-mono tabular-nums"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Code à 6 chiffres de votre application d'authentification, ou un code de secours.
              </p>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="login-trust-device-label">
              <input
                type="checkbox"
                data-testid="login-trust-device"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
              />
              <span className="text-sm text-slate-600">Faire confiance à cet appareil pendant 30 jours</span>
            </label>
            <button
              type="submit"
              data-testid="login-totp-submit"
              disabled={pending || code.trim().length < 6}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50 transition-all"
            >
              <ShieldCheck className="h-4 w-4" />
              {pending ? "Vérification…" : "Vérifier"}
            </button>
            <button
              type="button"
              data-testid="login-back-to-password"
              onClick={() => { setStep("password"); setError(""); setCode(""); }}
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 space-y-4">
          {error && (
            <div data-testid="login-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <Label htmlFor="login-email" className="text-sm">E-mail</Label>
            <Input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
              className="mt-1 h-11 text-base"
            />
          </div>
          <div>
            <Label htmlFor="login-password" className="text-sm">Mot de passe</Label>
            <div className="relative mt-1">
              <Input
                id="login-password"
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 text-base pr-11"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={pending || !email.trim() || !password}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50 transition-all"
          >
            <LogIn className="h-4 w-4" />
            {pending ? "Connexion…" : "Se connecter"}
          </button>
        </form>
        )}
      </div>
    </div>
  );
};

export default Login;
